// Pure boundary mappings between the web's localStorage shape and the
// server's `User.routeStyleSettings` JSONB shape. Extracted from
// routeStyleSettingsCloudSync.ts so Jest can exercise them without
// pulling in the GraphQL client or DOM globals.
//
// Kept in lock-step with the matching iOS and Android cloud-sync
// helpers so all three clients agree on the wire format.

import type { RouteStyleSettings } from './routeStyleSettings';

export type WebTab = keyof Omit<RouteStyleSettings, 'hideMileMarkers'>;
export type CloudRouteType = 'route' | 'alt' | 'spur' | 'recording' | 'transport';

export const CLOUD_TO_WEB: Record<string, WebTab | undefined> = {
  route: 'main',
  alt: 'alt',
  spur: 'spur',
  recording: 'recording',
  // 'transport' intentionally omitted — iOS-only, ignored on web
};

export const WEB_TO_CLOUD: Record<WebTab, CloudRouteType> = {
  main: 'route',
  alt: 'alt',
  spur: 'spur',
  recording: 'recording',
};

/** Cloud route_type string → web tab key. `null` for `transport` and unknowns.
 *  Lower-cases its input to match iOS/Android case-insensitive behaviour. */
export function webTabForCloudType(cloudType: string | null | undefined): WebTab | null {
  if (!cloudType) return null;
  const tab = CLOUD_TO_WEB[cloudType.toLowerCase()];
  return tab ?? null;
}

/** Web tab key → cloud route_type string. */
export function cloudTypeForWebTab(tab: WebTab): CloudRouteType {
  return WEB_TO_CLOUD[tab];
}

/** Web percent (10–100) → cloud fraction (0.1–1.0). Clamps to [0, 1] so
 *  malformed local state can't send nonsense to the API. */
export function opacityToCloud(webPercent: number): number {
  return Math.max(0, Math.min(1, webPercent / 100));
}

/** Cloud fraction (0.1–1.0) → web percent (rounded int). */
export function opacityFromCloud(cloudFraction: number): number {
  return Math.round(cloudFraction * 100);
}
